import React from "react";
import { Star, ArrowRight, Truck, ShieldCheck } from "lucide-react";
import { Fireflies } from "@/components/effects/Fireflies";
import { Reveal } from "@/components/effects/Reveal";
import { T } from "@/context/SiteContent";

export const Hero = () => (
  <section id="top" className="relative min-h-[60vh] md:min-h-[100vh] overflow-hidden">
    {/* The painted bayou artwork itself */}
    <div className="hero-art" />

    {/* Subtle darkening at very top (behind nav) for legibility of nav */}
    <div className="absolute inset-x-0 top-0 h-24 z-[3] pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(4,10,15,0.55) 0%, transparent 100%)" }} />

    {/* Ripple rings on the water surface (the "water drops" — keep these!) */}
    <div className="ripple-ring" style={{ left: "48%", bottom: "35%" }} />
    <div className="ripple-ring" style={{ left: "62%", bottom: "28%", animationDelay: "1.5s" }} />
    <div className="ripple-ring" style={{ left: "38%", bottom: "22%", animationDelay: "2.8s" }} />

    {/* Fireflies drifting through */}
    <Fireflies count={45} className="z-[2]" />

    {/* Content: only supporting text + CTAs. The painted title stays untouched. */}
    <div className="container relative min-h-[60vh] md:min-h-[100vh] flex flex-col justify-between pt-24 pb-16" style={{ zIndex: 4 }}>
      <Reveal>
        <p className="eyebrow text-center pt-16" data-testid="hero-eyebrow"><T k="hero.eyebrow" fallback="Free delivery, everywhere." /></p>
      </Reveal>

      {/* Spacer — leaves the middle 60% of viewport for the painted logo */}
      <div className="flex-1" />

      {/* CTA & sub-copy floating over the water at the bottom */}
      <div className="text-center">
        <Reveal delay={0.35}>
          <p className="font-serif italic text-lg sm:text-xl md:text-3xl text-[#F6F1E4] max-w-md sm:max-w-lg md:max-w-2xl mx-auto leading-snug" data-testid="hero-title" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.7)" }}>
            <T k="hero.subtitle_prefix" fallback="Baby gear, delivered to your " />
            <span className="text-[#FFD166] not-italic glow-warm"><T k="hero.subtitle_highlight" fallback="Walt Disney World" /></span>
            <T k="hero.subtitle_suffix" fallback=" resort." />
          </p>
        </Reveal>
        <Reveal delay={0.55}>
          <div className="mt-5 md:mt-9 flex flex-wrap items-center justify-center gap-3 md:gap-4">
            <a href="#trip-setup"><button className="btn-primary text-base md:text-lg" data-testid="hero-book-cta">
              Reserve Your Gear <ArrowRight className="inline w-4 h-4 md:w-5 md:h-5 ml-1" />
            </button></a>
            <a href="#catalog"><button className="btn-ghost text-sm md:text-base" data-testid="hero-catalog-cta">Explore Gear</button></a>
          </div>
        </Reveal>
        <Reveal delay={0.75}>
          <div className="mt-5 md:mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 md:gap-x-8 md:gap-y-3 text-xs md:text-sm text-[#F6F1E4]" style={{ textShadow: "0 1px 8px rgba(0,0,0,0.9)" }}>
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#FFD166]" /> Hospital-grade sanitized</div>
            <div className="flex items-center gap-2"><Truck className="w-4 h-4 text-[#FFD166]" /> Free delivery, everywhere</div>
            <div className="flex items-center gap-2"><Star className="w-4 h-4 text-[#FFD166]" /> 500+ five-star families</div>
          </div>
        </Reveal>
      </div>
    </div>

    {/* Fade seam into the next section */}
    <div className="hero-bottom-fade" />

    {/* SEO H1 — screen-reader visible on all sizes; also visible on md+ where it
        sits below the hero art without disturbing the painted canvas. */}
    <div className="container relative pb-6" style={{ zIndex: 4 }}>
      <Reveal delay={0.15}>
        <h1 className="sr-only md:not-sr-only text-center font-serif text-2xl md:text-3xl text-[#F6F1E4]/80 mt-4" style={{ textShadow: "0 2px 20px rgba(0,0,0,0.95)" }}>
          Disney World &amp; Orlando Stroller Rentals Delivered to You
        </h1>
      </Reveal>
    </div>
  </section>
);
